import type { PlatformEvent } from '../types/events';
import type {
  DirectRunErrorCode,
  InboundFrame,
  PromptFrame,
  OutboundFrame,
  VoiceAudioInFrame,
  VoiceInterruptedFrame,
  VoiceStartedFrame,
  VoiceStoppedFrame,
} from '../types/direct-run-frames';
import type { SurfaceRef, AgentRef, FileRef, WsPresignTicket } from '../types';
import type { AskUserAnswer } from '../types/inbox';
import { spaceApi, wsPresignConversation } from '../services/api';
import { PromiseQueue } from './run-event-stream';
import { TERMINAL_EVENTS } from './run-events-constants';
import { eventDebug } from './event-debug';

/**
 * A persistent, per-conversation WebSocket to the agent runtime's warm
 * micro-VM. One connection is reused across turns (it lands back on the same
 * micro-VM when reconnecting with the same conversationId), with one run in
 * flight at a time.
 *
 * The socket carries ONLY the primary run's `agent.run.*` events. Sub-agent /
 * delegated / ask_user / non-run events continue to arrive via the fan-out
 * subscription — see the adapter (`useAsyncInvokeAdapter.ts`) which merges this
 * connection's per-run stream with a filtered fan-out stream.
 *
 * Lifecycle is driven lazily by `startRun`: it (re)connects if needed, sends
 * the prompt, waits for `accepted`, and returns a per-run event stream that
 * closes on the run's terminal event while the underlying socket stays open.
 */

// The direct-run socket rides CloudFront → AgentCore, which reaps idle sockets
// at ~30s (NOT API Gateway's 10-min idle — that's the fan-out socket in
// PlatformEventContext). Ping well under that window so a quiet socket (between
// prompts, or during a long no-delta tool call) stays warm.
const PING_INTERVAL_MS = 20_000; // 20s, ~10s margin under the ~30s reap
const PONG_TIMEOUT_MS = 10_000; // death timer; must stay < PING_INTERVAL_MS
// How long to wait for `accepted` after sending a prompt before giving up
// (→ adapter falls back to REST invoke for agentcore; claude has no fallback).
// Must comfortably exceed a COLD micro-VM start: the backend only emits
// `accepted` once the run is admitted on the VM, and a cold claude runtime can
// take ~15–30s to spin up. Too short here is what caused claude agents to time
// out the handshake and (previously) fall back to the wrong-runtime REST path.
const ACCEPT_TIMEOUT_MS = 45_000;
// Grace after a terminal event before closing the per-run stream, so late
// deltas / stop frames are still drained. Mirrors run-event-stream.ts.
const TERMINAL_GRACE_MS = 1_000;
// How long to wait for `voice_started` after sending `voice_start`. The
// micro-VM must open the provider socket (xAI), so allow a cold-ish start but
// stay under the text-run ACCEPT_TIMEOUT_MS.
const VOICE_START_TIMEOUT_MS = 30_000;

/**
 * The direct-run socket could not be established (presign or socket open failed)
 * or the backend rejected the prompt BEFORE any run started (a pre-run `error`
 * frame). No run is running server-side, so the adapter may safely fall back to
 * the REST invoke path.
 */
export class DirectRunUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DirectRunUnavailableError';
  }
}

/**
 * The socket connected and the prompt was SENT, but the run didn't start within
 * the timeout. A run MAY now be running server-side, so the adapter must NOT
 * fall back to REST — that would double-execute the prompt (one run per
 * transport). Surface as an error instead.
 */
export class DirectRunTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DirectRunTimeoutError';
  }
}

interface ConnectionDeps {
  /** Resolves the conversation's owning space, used ONLY for the best-effort
   *  REST cancel fallback. Presign + connect need no space — the backend
   *  derives it from the conversation — so this is optional and may be unset
   *  when the socket is eagerly pre-warmed before space data has loaded. */
  getSpaceId?: () => string | undefined;
  /** Sent on first presign for a freshly-created conversation; harmless on resume. */
  agentId?: string;
}

interface ActiveRun {
  runId: string;
  queue: PromiseQueue<PlatformEvent>;
  receivedTerminal: boolean;
  closeTimer: ReturnType<typeof setTimeout> | null;
}

interface PendingStart {
  resolve: (result: { run: ActiveRun; conversationId: string }) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export interface StartRunInput {
  prompt: string;
  surfaceRefs?: SurfaceRef[];
  agentRefs?: AgentRef[];
  attachments?: FileRef[];
  /** Answer a pending `ask_user` question over this live session instead of via
   *  REST `/answer`. The backend resolves the request + inbox item server-side
   *  before continuing the turn on this micro-VM. */
  askUserRequestId?: string;
  answers?: AskUserAnswer[];
  /** Closes the per-run stream when the user aborts (Stop). */
  signal?: AbortSignal;
}

export interface StartRunResult {
  runId: string;
  conversationId: string;
  stream: AsyncGenerator<PlatformEvent>;
}

/**
 * Everything the voice listener receives: the four voice wire frames, plus
 * `agent.run.*` event frames belonging to the voice run (re-tagged as
 * `voice_run_event` so they never enter the text-run PromiseQueue), plus
 * voice-start rejections and socket teardown.
 */
export type VoiceConnectionEvent =
  | VoiceStartedFrame
  | VoiceAudioInFrame
  | VoiceInterruptedFrame
  | VoiceStoppedFrame
  | { type: 'voice_run_event'; eventType: string; data: Record<string, unknown> }
  | { type: 'voice_error'; code: DirectRunErrorCode; message: string }
  | { type: 'connection_closed' };

interface PendingVoiceStart {
  resolve: (frame: VoiceStartedFrame) => void;
  reject: (err: VoiceStartError) => void;
  timer: ReturnType<typeof setTimeout>;
}

/** `voice_start` was rejected (error frame) or timed out. `code` is the wire
 *  error code (`VOICE_CREDENTIAL_MISSING`, `VOICE_DISABLED`, `BUSY`, …) or
 *  `TIMEOUT` / `CONNECTION_CLOSED` for local failures. */
export class VoiceStartError extends Error {
  constructor(
    readonly code: DirectRunErrorCode | 'TIMEOUT' | 'CONNECTION_CLOSED',
    message: string,
  ) {
    super(message);
    this.name = 'VoiceStartError';
  }
}

export class DirectRunConnection {
  private ws: WebSocket | null = null;
  private ticket: WsPresignTicket | null = null;
  private connectPromise: Promise<void> | null = null;
  private pingTimer: ReturnType<typeof setTimeout> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private chunks = new Map<string, { total: number; parts: string[] }>();
  private activeRun: ActiveRun | null = null;
  private pendingStart: PendingStart | null = null;
  private voiceListener: ((e: VoiceConnectionEvent) => void) | null = null;
  private pendingVoiceStart: PendingVoiceStart | null = null;
  private activeVoiceRunId: string | null = null;
  private eventSeq = 0;
  private closed = false;

  constructor(
    readonly conversationId: string,
    private deps: ConnectionDeps,
  ) {}

  /** Is the underlying socket currently open? Used by callers (e.g. the inline
   *  ask_user card) to decide whether to answer over this live session vs REST. */
  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /** Merge in richer deps (e.g. the adapter's live `getSpaceId` + real
   *  `agentId`) over the ones a prior eager `prewarm` registered. */
  updateDeps(deps: ConnectionDeps): void {
    this.deps = {
      getSpaceId: deps.getSpaceId ?? this.deps.getSpaceId,
      agentId: deps.agentId ?? this.deps.agentId,
    };
  }

  /**
   * Start a run on the socket. The run is established by the FIRST frame that
   * carries its id — an `accepted` frame OR the first `event` frame (the claude
   * runtime streams an `agent.connected` event before `accepted`). Establishing
   * on first frame means no early events are dropped.
   *
   * Throws:
   *  - `DirectRunUnavailableError` if the connection can't be established or the
   *    backend rejects the prompt pre-run → adapter may fall back to REST.
   *  - `DirectRunTimeoutError` if connected + prompt sent but no run started in
   *    time → a run may be running, so the adapter must NOT fall back to REST.
   */
  async startRun(input: StartRunInput): Promise<StartRunResult> {
    if (this.closed) throw new DirectRunUnavailableError('DirectRunConnection is closed');
    if (this.activeRun && !this.activeRun.receivedTerminal) {
      throw new Error('A run is already in flight on this connection');
    }
    if (this.activeVoiceRunId || this.pendingVoiceStart) {
      // Mirror of the server's BUSY: one voice session OR one text run.
      throw new Error('A voice session is active on this connection');
    }
    // Drop the prior (terminal) run's bookkeeping so THIS run establishes
    // cleanly. The socket is reused across turns, and on a warm socket the
    // backend sends no fresh `accepted` for follow-up prompts AND may reuse the
    // previous runId — leaving a stale `activeRun` here would block establishment
    // (the new run's events would route to the old, closed stream) and the start
    // would time out. See `establishRun` / the `event` frame handler.
    this.activeRun = null;

    try {
      await this.ensureConnected();
    } catch (err) {
      throw new DirectRunUnavailableError(
        err instanceof Error ? err.message : String(err),
      );
    }

    const { run, conversationId } = await this.sendPromptAndAwaitStart(input);

    // Close the per-run stream on abort (Stop). The socket stays open.
    input.signal?.addEventListener(
      'abort',
      () => {
        if (this.activeRun === run && !run.receivedTerminal) {
          run.receivedTerminal = true;
          run.queue.close();
        }
      },
      { once: true },
    );

    return {
      runId: run.runId,
      conversationId,
      stream: this.runStream(run.queue),
    };
  }

  /** Establish the active run from the first frame carrying its id (idempotent:
   *  a later `accepted` after an establishing event is a no-op). Resolves the
   *  pending `startRun` with the run's stream queue. */
  private establishRun(runId: string, conversationId: string): void {
    if (this.activeRun && !this.activeRun.receivedTerminal) return;
    const pending = this.pendingStart;
    if (!pending) return;
    clearTimeout(pending.timer);
    this.pendingStart = null;
    const run: ActiveRun = {
      runId,
      queue: new PromiseQueue<PlatformEvent>(),
      receivedTerminal: false,
      closeTimer: null,
    };
    this.activeRun = run;
    pending.resolve({ run, conversationId });
  }

  private async *runStream(queue: PromiseQueue<PlatformEvent>): AsyncGenerator<PlatformEvent> {
    for await (const event of queue) yield event;
  }

  /** Open the socket ahead of the first prompt so the micro-VM is warm by the
   *  time the user sends. Best-effort — a failure here just means `startRun`
   *  reconnects (or falls back to REST) later. */
  prewarm(): Promise<void> {
    return this.ensureConnected().catch((err) => {
      eventDebug.log('direct-ws', 'prewarm failed (will retry on send)', {
        conversationId: this.conversationId,
        error: err instanceof Error ? err.message : String(err),
      });
    });
  }

  // ─── Voice sessions ────────────────────────────────────────────────────────

  /** True while a voice session is live on this socket. */
  get hasActiveVoiceSession(): boolean {
    return this.activeVoiceRunId !== null;
  }

  /** Register THE voice listener (one per connection — the session store).
   *  Returns an unsubscribe; unsubscribing a superseded listener is a no-op. */
  onVoiceEvent(listener: (e: VoiceConnectionEvent) => void): () => void {
    this.voiceListener = listener;
    return () => {
      if (this.voiceListener === listener) this.voiceListener = null;
    };
  }

  /**
   * Start a voice session on the socket. Resolves with the `voice_started`
   * frame; rejects with `VoiceStartError` on an `error` frame
   * (`VOICE_CREDENTIAL_MISSING`, `VOICE_DISABLED`, `BUSY`, …), timeout, or
   * socket teardown. One voice session OR one text run per connection — the
   * server enforces this with `BUSY`; we mirror it locally.
   */
  async startVoice(): Promise<VoiceStartedFrame> {
    if (this.closed) throw new VoiceStartError('CONNECTION_CLOSED', 'connection is closed');
    if (this.activeRun && !this.activeRun.receivedTerminal) {
      throw new VoiceStartError('BUSY', 'A text run is in flight on this connection');
    }
    if (this.activeVoiceRunId || this.pendingVoiceStart) {
      throw new VoiceStartError('BUSY', 'A voice session is already active');
    }
    try {
      await this.ensureConnected();
    } catch (err) {
      throw new VoiceStartError(
        'CONNECTION_CLOSED',
        err instanceof Error ? err.message : String(err),
      );
    }
    return new Promise<VoiceStartedFrame>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingVoiceStart = null;
        reject(new VoiceStartError('TIMEOUT', 'Timed out waiting for the voice session to start'));
      }, VOICE_START_TIMEOUT_MS);
      this.pendingVoiceStart = { resolve, reject, timer };
      this.send({ type: 'voice_start' });
    });
  }

  /** One mic chunk (base64 PCM16 mono @ 16 kHz). No-op when the socket isn't
   *  open — the server also drops chunks outside a session. */
  sendVoiceAudio(chunk: string): void {
    this.send({ type: 'voice_audio', chunk });
  }

  /** Typed user turn for the live voice session — the agent replies in
   *  speech. Same drop semantics as audio outside a session. */
  sendVoiceText(text: string): void {
    this.send({ type: 'voice_text', text });
  }

  /** End the voice session gracefully; the server replies `voice_stopped`,
   *  which drives the listener-side teardown. */
  stopVoice(): void {
    this.send({ type: 'voice_stop' });
  }

  private failPendingVoiceStart(err: VoiceStartError): void {
    const pending = this.pendingVoiceStart;
    if (!pending) return;
    clearTimeout(pending.timer);
    this.pendingVoiceStart = null;
    pending.reject(err);
  }

  private emitVoice(event: VoiceConnectionEvent): void {
    this.voiceListener?.(event);
  }

  /** Cancel the in-flight run (best-effort over the socket, REST as backup). */
  cancel(runId: string): void {
    this.send({ type: 'cancel' });
    const spaceId = this.deps.getSpaceId?.();
    if (spaceId) {
      spaceApi(spaceId).runs.cancel(runId).catch(() => {/* best-effort */});
    }
  }

  /** Tear down the socket and timers. Subsequent startRun would reconnect. */
  close(): void {
    this.closed = true;
    this.clearTimers();
    this.failPendingStart(new DirectRunUnavailableError('connection closed'));
    this.failPendingVoiceStart(new VoiceStartError('CONNECTION_CLOSED', 'connection closed'));
    if (this.activeVoiceRunId) {
      this.activeVoiceRunId = null;
      this.emitVoice({ type: 'connection_closed' });
    }
    if (this.activeRun && !this.activeRun.receivedTerminal) {
      this.activeRun.receivedTerminal = true;
      this.activeRun.queue.close();
    }
    this.activeRun = null;
    const ws = this.ws;
    this.ws = null;
    try {
      ws?.close();
    } catch {/* ignore */}
  }

  // ─── Connection management ─────────────────────────────────────────────────

  private async ensureConnected(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
    if (this.connectPromise) return this.connectPromise;
    this.connectPromise = this.connect().finally(() => {
      this.connectPromise = null;
    });
    return this.connectPromise;
  }

  private async connect(): Promise<void> {
    // Presign is un-scoped — the backend resolves the space from the
    // conversation — so this needs no spaceId and can run before any space or
    // list data has loaded.
    if (!this.ticket || this.ticketExpired(this.ticket)) {
      this.ticket = await wsPresignConversation(
        this.conversationId,
        this.deps.agentId ? { agentId: this.deps.agentId } : {},
      );
    }

    // wsPath is host-less; the WebSocket constructor resolves it same-origin
    // (https→wss). CloudFront proxies `/runtimes/*` to AgentCore.
    const ws = new WebSocket(this.ticket.wsPath);
    this.ws = ws;

    await new Promise<void>((resolve, reject) => {
      const onOpen = () => {
        ws.removeEventListener('error', onError);
        this.startKeepalive();
        eventDebug.log('direct-ws', 'connected', { conversationId: this.conversationId });
        resolve();
      };
      const onError = () => {
        ws.removeEventListener('open', onOpen);
        reject(new Error('Direct run socket failed to open'));
      };
      ws.addEventListener('open', onOpen, { once: true });
      ws.addEventListener('error', onError, { once: true });
    });

    ws.addEventListener('message', (e) => this.onMessage(e));
    ws.addEventListener('close', () => this.onClose());
  }

  private ticketExpired(ticket: WsPresignTicket): boolean {
    // expiresAt is epoch ms (number); `new Date()` also accepts an ISO string.
    const expires = new Date(ticket.expiresAt).getTime();
    return Number.isFinite(expires) && expires <= Date.now();
  }

  private onClose(): void {
    this.clearTimers();
    this.ws = null;
    // A dead ticket can't reopen — force a re-presign on the next startRun.
    this.ticket = null;
    // Closed before the run was established → treat as connection unavailable so
    // the adapter can fall back to REST (no run started server-side).
    this.failPendingStart(new DirectRunUnavailableError('Direct run socket closed'));
    this.failPendingVoiceStart(
      new VoiceStartError('CONNECTION_CLOSED', 'Direct run socket closed'),
    );
    // A live voice session dies with its socket (no reconnect/resume in v1) —
    // tell the listener so it stops the mic and flushes playback.
    if (this.activeVoiceRunId) {
      this.activeVoiceRunId = null;
      this.emitVoice({ type: 'connection_closed' });
    }
    // End the in-flight run's stream without a terminal so the adapter falls
    // back to the (unfiltered) fan-out + poll path for this runId.
    if (this.activeRun && !this.activeRun.receivedTerminal) {
      this.activeRun.receivedTerminal = true;
      this.activeRun.queue.close();
    }
  }

  private startKeepalive(): void {
    this.clearTimers();
    this.scheduleIdlePing();
  }

  /**
   * Idle-only keepalive: ping ONLY after the socket has been quiet for
   * `PING_INTERVAL_MS`. Every inbound frame reschedules this (see `onMessage`),
   * so while events are streaming we send no pings at all — we only ping a
   * genuinely idle socket (between turns, or during a quiet cold-start wait) to
   * keep the warm micro-VM from being reaped.
   */
  private scheduleIdlePing(): void {
    if (this.pingTimer) clearTimeout(this.pingTimer);
    this.pingTimer = setTimeout(() => {
      this.send({ type: 'ping' });
      // Arm the death timer: if the idle ping draws no reply, the socket is dead.
      if (this.pongTimer) clearTimeout(this.pongTimer);
      this.pongTimer = setTimeout(() => {
        try {
          this.ws?.close();
        } catch {/* ignore */}
      }, PONG_TIMEOUT_MS);
    }, PING_INTERVAL_MS);
  }

  private clearTimers(): void {
    if (this.pingTimer) clearTimeout(this.pingTimer);
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.pingTimer = null;
    this.pongTimer = null;
  }

  // ─── Prompt / accept handshake ─────────────────────────────────────────────

  private sendPromptAndAwaitStart(
    input: StartRunInput,
  ): Promise<{ run: ActiveRun; conversationId: string }> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingStart = null;
        // The prompt was sent; a run may have started server-side. The caller
        // must NOT fall back to REST (it would double-execute).
        reject(new DirectRunTimeoutError('Timed out waiting for the run to start'));
      }, ACCEPT_TIMEOUT_MS);
      this.pendingStart = { resolve, reject, timer };

      const frame: PromptFrame = {
        type: 'prompt',
        prompt: input.prompt,
        ...(input.surfaceRefs?.length ? { surfaceRefs: input.surfaceRefs } : {}),
        ...(input.agentRefs?.length ? { agentRefs: input.agentRefs } : {}),
        ...(input.attachments?.length ? { attachments: input.attachments } : {}),
        ...(input.askUserRequestId ? { askUserRequestId: input.askUserRequestId } : {}),
        ...(input.answers?.length ? { answers: input.answers } : {}),
      };
      this.send(frame);
    });
  }

  private failPendingStart(err: Error): void {
    const pending = this.pendingStart;
    if (!pending) return;
    clearTimeout(pending.timer);
    this.pendingStart = null;
    pending.reject(err);
  }

  // ─── Inbound frames ────────────────────────────────────────────────────────

  private onMessage(e: MessageEvent): void {
    // Any inbound traffic means the socket is alive and busy: clear the pending
    // death timer and push the next idle-ping out. While frames keep arriving we
    // never ping — we only ping a socket that's been quiet for PING_INTERVAL_MS.
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
    this.scheduleIdlePing();
    let frame: InboundFrame;
    try {
      frame = JSON.parse(typeof e.data === 'string' ? e.data : '') as InboundFrame;
    } catch {
      return;
    }
    this.handleFrame(frame);
  }

  private handleFrame(frame: InboundFrame): void {
    switch (frame.type) {
      case 'chunk': {
        const entry =
          this.chunks.get(frame.id) ?? { total: frame.total, parts: [] as string[] };
        entry.parts[frame.seq] = frame.data;
        this.chunks.set(frame.id, entry);
        if (entry.parts.filter(Boolean).length === entry.total) {
          this.chunks.delete(frame.id);
          try {
            this.handleFrame(JSON.parse(atob(entry.parts.join(''))) as InboundFrame);
          } catch {/* malformed reassembly — drop */}
        }
        return;
      }
      case 'accepted': {
        this.establishRun(frame.runId, frame.conversationId);
        eventDebug.log('direct-ws', 'run accepted', { runId: frame.runId });
        return;
      }
      case 'event': {
        // While awaiting a run start, the first runId-bearing frame establishes
        // it — an `accepted` frame OR the first run event. The claude runtime
        // streams `agent.connected` before `accepted` on a fresh socket, and on
        // a warm socket sends NO `accepted` at all for follow-up prompts (and may
        // reuse the prior runId), so we must not gate this on `!this.activeRun`.
        // (`pendingStart` is only set when no run is in flight — see `startRun`.)
        if (this.pendingStart && frame.runId) {
          this.establishRun(frame.runId, frame.conversationId ?? this.conversationId);
        }
        this.dispatchEvent(frame.eventType, frame.runId, frame.conversationId, frame.data);
        return;
      }
      case 'voice_started': {
        const pending = this.pendingVoiceStart;
        if (pending) {
          clearTimeout(pending.timer);
          this.pendingVoiceStart = null;
        }
        this.activeVoiceRunId = frame.runId;
        eventDebug.log('direct-ws', 'voice session started', { runId: frame.runId });
        pending?.resolve(frame);
        this.emitVoice(frame);
        return;
      }
      case 'voice_audio':
      case 'voice_interrupted': {
        this.emitVoice(frame);
        return;
      }
      case 'voice_stopped': {
        this.activeVoiceRunId = null;
        eventDebug.log('direct-ws', 'voice session stopped', {
          runId: frame.runId,
          reason: frame.reason,
        });
        this.emitVoice(frame);
        return;
      }
      case 'error': {
        // Voice-start rejection (VOICE_CREDENTIAL_MISSING, VOICE_DISABLED, BUSY,
        // …) → reject the voice handshake; the session store maps the code to
        // user-facing copy.
        if (this.pendingVoiceStart) {
          this.failPendingVoiceStart(new VoiceStartError(frame.code, frame.message));
          return;
        }
        // Mid-voice-session error → forward to the voice listener (the server
        // normally follows with `voice_stopped`, but don't rely on it).
        if (this.activeVoiceRunId) {
          this.emitVoice({ type: 'voice_error', code: frame.code, message: frame.message });
          return;
        }
        // Pre-run error → reject the handshake as "unavailable" (no run started;
        // adapter may fall back to REST).
        if (this.pendingStart) {
          this.failPendingStart(
            new DirectRunUnavailableError(`${frame.code}: ${frame.message}`),
          );
          return;
        }
        // Mid-run error → surface as a synthetic failed event so the reducer
        // terminates the run cleanly.
        const run = this.activeRun;
        if (run && !run.receivedTerminal) {
          this.dispatchEvent('agent.run.failed', run.runId, this.conversationId, {
            runId: run.runId,
            error: frame.message,
            code: frame.code,
          });
        }
        return;
      }
      case 'pong':
        return;
    }
  }

  /** Convert an `event` frame into the `PlatformEvent` shape the reducer
   *  consumes and push it onto the active run's stream. */
  private dispatchEvent(
    eventType: string,
    runId: string,
    conversationId: string | undefined,
    data: Record<string, unknown>,
  ): void {
    // Voice-run events (voice_transcript*, tool_call/result during a session)
    // go to the voice listener, never into the text-run PromiseQueue.
    if (this.activeVoiceRunId && runId === this.activeVoiceRunId) {
      this.emitVoice({ type: 'voice_run_event', eventType, data });
      return;
    }
    const run = this.activeRun;
    if (!run || run.runId !== runId) return;

    const event: PlatformEvent = {
      eventId: `direct-${runId}-${this.eventSeq++}`,
      eventType,
      source: 'direct-ws',
      subjectId: runId,
      data: { conversationId, ...data },
      metadata: { timestamp: new Date().toISOString() },
      orgId: '',
    };

    if (run.receivedTerminal && TERMINAL_EVENTS.has(eventType)) return;
    run.queue.push(event);
    eventDebug.recordRunEvent(runId, eventType);

    // The primary run's terminal (no parentRunId) closes the per-run stream
    // after a grace window for late deltas. The socket itself stays open.
    if (TERMINAL_EVENTS.has(eventType) && !(data as { parentRunId?: string }).parentRunId) {
      run.receivedTerminal = true;
      eventDebug.recordTerminal(runId, eventType, 'ws');
      run.closeTimer = setTimeout(() => run.queue.close(), TERMINAL_GRACE_MS);
    }
  }

  private send(frame: OutboundFrame): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
    }
  }
}

// ─── Module registry (keyed by conversationId) ───────────────────────────────
// Held at module scope so a persistent socket survives adapter re-creation and
// re-renders, mirroring the `runStateStore` singleton in useAsyncInvokeAdapter.

const connections = new Map<string, DirectRunConnection>();
const pendingCloses = new Map<string, ReturnType<typeof setTimeout>>();

// Grace before a closeConnection() actually tears down the socket. A
// re-acquire (getOrCreateConnection) within the window cancels it. This makes
// the lifecycle resilient to React StrictMode's synthetic mount→unmount→mount
// in dev (which would otherwise close the eagerly-prewarmed socket right after
// it connects, forcing a cold reconnect on the first send) while still closing
// promptly on a real conversation switch / unmount.
const CLOSE_GRACE_MS = 1_000;

export function getOrCreateConnection(
  conversationId: string,
  deps: ConnectionDeps,
): DirectRunConnection {
  // Cancel a pending close — we're (re-)acquiring this connection.
  const pending = pendingCloses.get(conversationId);
  if (pending) {
    clearTimeout(pending);
    pendingCloses.delete(conversationId);
  }
  let conn = connections.get(conversationId);
  if (!conn) {
    conn = new DirectRunConnection(conversationId, deps);
    connections.set(conversationId, conn);
  } else {
    // Reused (e.g. adapter call after an eager prewarm) — adopt the newer deps.
    conn.updateDeps(deps);
  }
  return conn;
}

/** True iff a connection for this conversation already exists AND its socket is
 *  open. Pure probe — never creates a connection (unlike getOrCreateConnection),
 *  so a caller can branch on "do we have a live session?" without warming one. */
export function hasOpenConnection(conversationId: string | undefined): boolean {
  if (!conversationId) return false;
  const conn = connections.get(conversationId);
  return !!conn && conn.isOpen;
}

export function closeConnection(conversationId: string | undefined): void {
  if (!conversationId) return;
  if (pendingCloses.has(conversationId)) return; // already scheduled
  if (!connections.has(conversationId)) return;
  const timer = setTimeout(() => {
    pendingCloses.delete(conversationId);
    const conn = connections.get(conversationId);
    if (!conn) return;
    connections.delete(conversationId);
    conn.close();
  }, CLOSE_GRACE_MS);
  pendingCloses.set(conversationId, timer);
}
